import { FactorType } from '@bp/planung-graphql-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FactorEpochModal } from '../../../../components/Factors/modals/FactorEpochModal';
import { FactorInheritModal } from '../../../../components/Factors/modals/FactorInheritModal';
import { FactorSubjectModal } from '../../../../components/Factors/modals/FactorSubjectModal';
import { FactorTeacherModal } from '../../../../components/Factors/modals/FactorTeacherModal';
import { FactorsTable } from '../../../../components/Factors/tables/FactorsTable';
import { Page } from '../../../../components/Page/Page';
import { useConfirm } from '../../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import {
  useDeleteTeachingLoadFactorMutation,
  useUpdateTeachingLoadFactorMutation,
} from '../../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserErrorToast } from '../../../../utils/toast';

export const FactorsPage = () => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('TEACHINGLOADFACTOR');
  const permissions = usePermissionChecker();

  const [loading, setLoading] = useState<boolean>(false);

  const [, deleteTeachingLoadFactor] = useDeleteTeachingLoadFactorMutation();
  const [, updateTeachingLoadFactor] = useUpdateTeachingLoadFactorMutation();

  const [teachingLoadFactorUuid, setTeachingLoadFactorUuid] = useState<string | null>(null);
  const [subjectModalOpen, setSubjectModalOpen] = useState<boolean>(false);
  const [epochModalOpen, setEpochModalOpen] = useState<boolean>(false);
  const [teacherModalOpen, setTeacherModalOpen] = useState<boolean>(false);
  const [inheritModalOpen, setInheritModalOpen] = useState<boolean>(false);

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm();

  function handleAdd(type: FactorType) {
    setTeachingLoadFactorUuid(null);
    switch (type) {
      case FactorType.Base:
        setSubjectModalOpen(true);
        break;
      case FactorType.Time:
        setEpochModalOpen(true);
        break;
      case FactorType.Person:
        setTeacherModalOpen(true);
        break;
      default:
        break;
    }
  }

  function handleEdit(uuid: string, type: FactorType) {
    setTeachingLoadFactorUuid(uuid);
    switch (type) {
      case FactorType.Base:
        setSubjectModalOpen(true);
        break;
      case FactorType.Time:
        setEpochModalOpen(true);
        break;
      case FactorType.Person:
        setTeacherModalOpen(true);
        break;
      default:
        break;
    }
  }

  async function handleToggleActive(elements: { uuid: string; active: boolean }[]) {
    setLoading(true);
    for (const { uuid, active } of elements) {
      await updateTeachingLoadFactor(
        {
          update: { active },
          where: {
            uuid: uuid,
          },
        },
        context,
      );
    }
    setLoading(false);
  }

  async function handleDelete(uuids: string[]) {
    const confirmResult = await confirmDelete({
      message: t('delete.message', {
        type: t('factors.title', { count: uuids.length }),
        context: 'male',
        count: uuids.length,
      }),
    });
    if (!confirmResult) return;

    setLoading(true);

    const { error } = await deleteTeachingLoadFactor({ where: { uuid_IN: uuids } }, context);

    if (error) {
      showUserErrorToast({ error });
    } else {
      showSuccessDeleteToast();
    }

    setLoading(false);
  }

  return (
    <Page isForbidden={!permissions?.canReadDeputate()} title={t('lessonUnit.factors')}>
      <FactorsTable
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onInherit={() => setInheritModalOpen(true)}
        onToggleActive={handleToggleActive}
        onDelete={handleDelete}
      />

      <FactorSubjectModal
        selectedFactorUuid={teachingLoadFactorUuid}
        isOpen={subjectModalOpen}
        onClose={() => {
          setSubjectModalOpen(false);
          setTeachingLoadFactorUuid(null);
        }}
      />

      <FactorEpochModal
        selectedFactorUuid={teachingLoadFactorUuid}
        isOpen={epochModalOpen}
        onClose={() => {
          setEpochModalOpen(false);
          setTeachingLoadFactorUuid(null);
        }}
      />

      <FactorTeacherModal
        selectedFactorUuid={teachingLoadFactorUuid}
        isOpen={teacherModalOpen}
        onClose={() => {
          setTeacherModalOpen(false);
          setTeachingLoadFactorUuid(null);
        }}
      />

      <FactorInheritModal isOpen={inheritModalOpen} onClose={() => setInheritModalOpen(false)} />

      <ConfirmationDialog />
    </Page>
  );
};
